/**
 * PageHeader — React (web) implementation.
 *
 * Leading back control, centered title (heading semantics: role="heading" +
 * aria-level), optional trailing close control. The controls are the shared
 * design-system `Button` (size M, Secondary, icon-only) so sizing, the circular
 * secondary treatment, analytics activation, and button accessibility stay
 * centralized (Figma "Sub page" header). The back control is always present;
 * the close control renders only when the host supplies both a handler
 * (`onClose`) and the trailing `close` label — a control that would render
 * without a handler or without an accessibility label is never rendered as an
 * interactive button. When the trailing control is absent the trailing side
 * renders a same-width spacer so the title stays optically centered. Owns only
 * its internal layout (ADR-020 Rule 2).
 */

import React from 'react';
import { spacing } from '../../elements/spacing';
import { typography } from '../../elements/typography';
import { Button } from '../Button/Button.web';
import { DEFAULT_HEADING_ARIA_LEVEL } from '../Text/Text';
import type { PageHeaderProps } from './PageHeader';
import {
  PAGE_HEADER_BACK_ICON,
  PAGE_HEADER_CLOSE_ICON,
  PAGE_HEADER_CONTROL_SIZE,
  PAGE_HEADER_TITLE_COLOR,
} from './PageHeader';

export interface WebPageHeaderProps extends PageHeaderProps {
  onBack: () => void;
  onClose?: () => void;
  backActionId?: string;
  closeActionId?: string;
  'data-testid'?: string;
}

export function PageHeader({
  title,
  backAccessibilityLabel,
  close,
  onBack,
  onClose,
  backActionId = 'ui.page-header.back',
  closeActionId = 'ui.page-header.close',
  titleNumberOfLines = 1,
  testID,
  'data-testid': dataTestId,
}: WebPageHeaderProps) {
  const rootTestId = dataTestId ?? testID;

  const rootStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: PAGE_HEADER_CONTROL_SIZE,
    gap: spacing.s,
  };
  // Single line keeps the original nowrap + tail-ellipsis; a higher cap clamps
  // to N lines (the web analog of native numberOfLines) so long titles wrap
  // instead of truncating.
  const multiLine = titleNumberOfLines > 1;
  const titleStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: typography.h2Bold.fontFamily,
    fontWeight: typography.h2Bold.fontWeight,
    fontSize: typography.h2Bold.fontSize,
    lineHeight: `${typography.h2Bold.lineHeight}px`,
    letterSpacing: typography.h2Bold.letterSpacing,
    color: PAGE_HEADER_TITLE_COLOR,
    margin: 0,
    ...(multiLine
      ? {
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: titleNumberOfLines,
          whiteSpace: 'normal',
        }
      : { whiteSpace: 'nowrap' }),
  };
  const spacerStyle: React.CSSProperties = {
    width: PAGE_HEADER_CONTROL_SIZE,
    height: PAGE_HEADER_CONTROL_SIZE,
    flexShrink: 0,
  };

  return (
    <div style={rootStyle} data-testid={rootTestId}>
      <Button
        size="M"
        type="Secondary"
        icon
        text={false}
        iconName={PAGE_HEADER_BACK_ICON}
        onPress={onBack}
        actionId={backActionId}
        accessibilityLabel={backAccessibilityLabel}
        data-testid={rootTestId ? `${rootTestId}-back` : undefined}
      />
      <span
        role="heading"
        aria-level={DEFAULT_HEADING_ARIA_LEVEL}
        style={titleStyle}
        data-testid={rootTestId ? `${rootTestId}-title` : undefined}
      >
        {title}
      </span>
      {onClose && close ? (
        <Button
          size="M"
          type="Secondary"
          icon
          text={false}
          iconName={PAGE_HEADER_CLOSE_ICON}
          onPress={onClose}
          actionId={closeActionId}
          accessibilityLabel={close.accessibilityLabel}
          data-testid={rootTestId ? `${rootTestId}-close` : undefined}
        />
      ) : (
        <span aria-hidden="true" style={spacerStyle} />
      )}
    </div>
  );
}
